import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';

const PageContainer = styled(motion.div)`
  padding: 100px 5% 50px 5%;
  min-height: 100vh;
  background: var(--background-start);
`;

const PageContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(20px);
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 30px;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContentArea = styled.div`
  color: var(--primary-color);
  line-height: 1.8;
  font-size: 1.1rem;
  
  h1, h2, h3, h4, h5, h6 {
    color: var(--primary-color);
    margin: 25px 0 15px 0;
    font-weight: 600;
  }
  
  h2 {
    font-size: 1.8rem;
    border-bottom: 2px solid var(--gradient-pink);
    padding-bottom: 10px;
    margin-top: 40px;
  }
  
  h3 {
    font-size: 1.4rem;
    color: var(--gradient-cyan);
  }
  
  h4 {
    font-size: 1.2rem;
    margin-top: 20px;
  }
  
  p {
    margin: 15px 0;
    text-align: justify;
  }
  
  ul, ol {
    margin: 15px 0;
    padding-left: 25px;
  }
  
  li {
    margin: 8px 0;
    color: var(--secondary-color);
  }
  
  strong {
    color: var(--gradient-pink);
    font-weight: 600;
  }
  
  em {
    color: var(--secondary-color);
    font-style: italic;
  }
  
  a {
    color: var(--gradient-cyan);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: all 0.3s ease;
    
    &:hover {
      border-bottom-color: var(--gradient-cyan);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    
    h2 {
      font-size: 1.5rem;
    }
    
    h3 {
      font-size: 1.2rem;
    }
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 60px;
  color: var(--secondary-color);
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px;
  color: #ff4757;
  font-size: 1.2rem;
  background: rgba(255, 71, 87, 0.1);
  border: 1px solid #ff4757;
  border-radius: 12px;
`;

const DynamicPage = () => {
  const { page } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_PAGES)}?page=${page}`);
        
        if (!response.ok) {
          throw new Error('Sayfa yüklenemedi');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setPageData(data.page);
        } else {
          setError(data.message || 'Sayfa bulunamadı');
        }
      } catch (error) {
        console.error('Sayfa yüklenirken hata:', error);
        setError('Sayfa yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    if (page) {
      fetchPageData();
    }
  }, [page]);

  if (loading) {
    return (
      <PageContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Header />
        <PageContent>
          <LoadingText>Sayfa yükleniyor...</LoadingText>
        </PageContent>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Header />
        <PageContent>
          <ErrorMessage>{error}</ErrorMessage>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header />
      <PageContent>
        <PageTitle>{pageData?.title || 'Sayfa'}</PageTitle>
        <ContentArea 
          dangerouslySetInnerHTML={{ 
            __html: pageData?.content || '<p>İçerik bulunamadı.</p>' 
          }} 
        />
      </PageContent>
    </PageContainer>
  );
};

export default DynamicPage;
